import { useState } from "react";
import { Phone, Mail, MapPin, Clock, Send } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

const WEB3FORMS_KEY = "0f464278-cd6f-4ef5-89e5-c829a77a721c";

const CONTACT_INFO = [
  { icon: Phone, label: "Phone", value: "0413 621 730", href: "tel:+61413621730" },
  { icon: Mail, label: "Email", value: "info@solidstep.com.au", href: "mailto:info@solidstep.com.au" },
  { icon: MapPin, label: "Service Area", value: "Melbourne & Surrounding Areas, VIC" },
  { icon: Clock, label: "Available", value: "24/7" },
];

export default function ContactPage() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in all required fields.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          access_key: WEB3FORMS_KEY,
          subject: `New Enquiry from ${form.name} - Solid Step Website`,
          from_name: "Solid Step Website",
          name: form.name,
          email: form.email,
          phone: form.phone || "Not provided",
          message: form.message,
        }),
      });
      const data = await res.json();
      if (data.success) {
        toast.success("Message sent! We'll get back to you soon.");
        setForm({ name: "", email: "", phone: "", message: "" });
      } else {
        toast.error("Something went wrong. Please try again.");
      }
    } catch {
      toast.error("Something went wrong. Please try again.");
    }
    setSubmitting(false);
  };

  return (
    <main data-testid="contact-page" className="pt-20">
      {/* Header */}
      <section className="py-20 md:py-28 bg-[#0a0a0a] border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <p className="font-sans uppercase text-xs tracking-[0.2em] text-[#D4AF37] mb-4">Get In Touch</p>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-white mb-4">
            Contact Us
          </h1>
          <p className="font-sans text-base text-[#A3A3A3] max-w-2xl mx-auto">
            Have a question or need a quote? Send us a message and we'll get back to you within 24 hours.
          </p>
        </div>
      </section>

      <section className="py-16 md:py-20 bg-[#050505]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Contact Info */}
            <div>
              <h3 className="font-serif text-2xl text-white mb-8">Contact Information</h3>
              <div className="space-y-6">
                {CONTACT_INFO.map((info, i) => (
                  <div key={i} data-testid={`contact-info-${i}`} className="flex items-start gap-4">
                    <div className="w-10 h-10 border border-[#D4AF37]/30 flex items-center justify-center flex-shrink-0">
                      <info.icon className="w-4 h-4 text-[#D4AF37]" />
                    </div>
                    <div>
                      <p className="font-sans text-xs uppercase tracking-wider text-[#D4AF37] mb-1">{info.label}</p>
                      {info.href ? (
                        <a href={info.href} className="font-sans text-white text-sm hover:text-[#D4AF37] transition-colors">
                          {info.value}
                        </a>
                      ) : (
                        <p className="font-sans text-white text-sm">{info.value}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-10 p-6 bg-[#121212] border border-[#D4AF37]/20">
                <h4 className="font-serif text-lg text-white mb-2">Free On-Site Assessment</h4>
                <p className="font-sans text-sm text-[#A3A3A3] leading-relaxed">
                  We offer complimentary on-site visits to measure your space and provide an accurate, detailed quote at no cost.
                </p>
              </div>
            </div>

            {/* Message Form */}
            <div className="lg:col-span-2">
              <div className="bg-[#121212] border border-white/5 p-8 md:p-10">
                <h3 className="font-serif text-2xl text-white mb-2">Send a Message</h3>
                <p className="font-sans text-sm text-[#A3A3A3] mb-8">Have a general enquiry? Drop us a message below.</p>
                <form onSubmit={handleSubmit} data-testid="contact-form" className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <Label className="text-white text-sm mb-2 block">Full Name *</Label>
                      <Input
                        data-testid="contact-name"
                        placeholder="John Smith"
                        value={form.name}
                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                        className="bg-[#050505] border-white/10 text-white placeholder:text-white/30 focus:border-[#D4AF37] rounded-none h-12"
                      />
                    </div>
                    <div>
                      <Label className="text-white text-sm mb-2 block">Email Address *</Label>
                      <Input
                        data-testid="contact-email"
                        type="email"
                        placeholder="john@example.com"
                        value={form.email}
                        onChange={(e) => setForm({ ...form, email: e.target.value })}
                        className="bg-[#050505] border-white/10 text-white placeholder:text-white/30 focus:border-[#D4AF37] rounded-none h-12"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-white text-sm mb-2 block">Phone (Optional)</Label>
                    <Input
                      data-testid="contact-phone"
                      placeholder="0400 000 000"
                      value={form.phone}
                      onChange={(e) => setForm({ ...form, phone: e.target.value })}
                      className="bg-[#050505] border-white/10 text-white placeholder:text-white/30 focus:border-[#D4AF37] rounded-none h-12"
                    />
                  </div>
                  <div>
                    <Label className="text-white text-sm mb-2 block">Message *</Label>
                    <Textarea
                      data-testid="contact-message"
                      placeholder="How can we help you?"
                      rows={5}
                      value={form.message}
                      onChange={(e) => setForm({ ...form, message: e.target.value })}
                      className="bg-[#050505] border-white/10 text-white placeholder:text-white/30 focus:border-[#D4AF37] rounded-none resize-none"
                    />
                  </div>
                  <button
                    type="submit"
                    data-testid="contact-submit"
                    disabled={submitting}
                    className="inline-flex items-center gap-2 bg-[#D4AF37] text-black font-semibold px-10 py-4 text-sm uppercase tracking-wider hover:bg-[#F0C74A] transition-colors disabled:opacity-50"
                  >
                    {submitting ? "Sending..." : "Send Message"}
                    <Send className="w-4 h-4" />
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
